import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { supabase } from '@/shared/lib/supabase';
import { recordGdprConsent } from '../api';
import { profileKeys } from '@/features/profile/api';
import { toast } from '@/shared/lib/toast';
import type { Participant } from '@shared/schemas/participants';
import { ConsentDialog } from './ConsentDialog';

interface Props {
  /**
   * El participante propio, **con el tipo que devuelve el esquema**, no uno
   * escrito a mano aquí.
   *
   * Ese tipo ad-hoc es *por qué* el fallo del 2026-08-28 llegó a compilar. Decía
   * `gdpr_consent_at?: string | null`, así que leer el campo era legal aunque
   * `participantSchema` no lo declarase — y lo que llegaba era `undefined` para
   * siempre. Con `Participant`, leer una propiedad que el esquema no tiene **no
   * compila**: el mismo fallo pasa de un día en producción a un error de `tsc`.
   *
   * `null | undefined` se queda porque `useMyProfile()` los devuelve mientras
   * carga, y eso hay que seguir contemplándolo.
   */
  profile: Participant | null | undefined;
}

/**
 * El consentimiento de quien **ya tiene cuenta y nunca lo dio**.
 *
 * ## Por qué hace falta, si el alta ya lo pide
 *
 * Porque `/login` es también una pantalla de alta y no lo pide. Monta los
 * mismos botones de Google y Microsoft, y el OAuth de Supabase crea la cuenta
 * si no existe: por ahí se entra sin ver la casilla ni una sola vez. Tampoco lo
 * tapa el servidor —`participants.gdpr_consent_at` es anulable, sin `CHECK` ni
 * trigger que la rellene, y ninguna policy la mira—, así que esas cuentas se
 * quedaban sin consentimiento para siempre y con acceso completo.
 *
 * El arreglo evidente —poner el diálogo también en `/login`— se lo comería
 * también a quien ya tiene cuenta y sólo quiere entrar, que son casi todas las
 * pulsaciones de ese botón. Preguntarlo **después** de entrar y sólo a quien le
 * falta sale una vez por persona y cubre todos los caminos: el SSO del acceso,
 * los enlaces de invitación y las cuentas viejas.
 *
 * A cambio, se pregunta con la cuenta ya creada. Es peor que pedirlo antes, y
 * es la razón de que quien no lo dé **salga de la sesión**: sin consentimiento
 * no se sigue usando, y en el siguiente acceso vuelve a preguntarse.
 *
 * ## Por qué el diálogo desaparece solo
 *
 * No hay estado local de «ya lo aceptó». La condición se lee del perfil, y al
 * aceptar se invalida el perfil: el diálogo se va cuando el servidor dice que
 * está registrado, no cuando el botón se pulsa. Si la RPC falla, el perfil
 * vuelve igual y el diálogo sigue ahí — que es exactamente lo que tiene que
 * pasar.
 *
 * **Pero seguir ahí en silencio no lo es**, y costó caro: la función de la base
 * de datos llevaba desde julio fallando en todas las llamadas (#57), y como
 * aquí se llamaba con `void` sobre un `supabase.rpc` —que no rechaza, resuelve
 * con `{ error }`— el botón dejaba la pantalla igual que si no se hubiera
 * pulsado. Con esta puerta puesta eso no era «no pasa nada»: era una cuenta
 * encerrada, porque lo único que quedaba era cerrar sesión. Ahora la llamada
 * es una mutación que lanza, así que el rechazo se ve —aviso a quien está
 * delante— y además entra por el `MutationCache.onError`, que lo registra en
 * `client_errors`.
 *
 * ## El modo demo queda fuera
 *
 * La cuenta demo la comparte todo el que entra a probar desde el escaparate y
 * no puede escribir: pedirle consentimiento sería un diálogo delante de la
 * demostración y un botón que no puede funcionar.
 */
export function ConsentGate({ profile }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const aceptar = useMutation({
    mutationFn: recordGdprConsent,
    onSuccess: () => qc.invalidateQueries({ queryKey: profileKeys.me() }),
    // Claves que ya existen en los ocho idiomas: un fallo de esta RPC no
    // necesita una redacción propia, necesita no ser invisible.
    onError: () => toast.error(t('common.error_boundary_title'), t('common.error_generic')),
  });

  const missing = Boolean(profile) && !profile?.is_demo && !profile?.gdpr_consent_at;
  if (!missing) return null;

  return (
    <ConsentDialog
      variant="gate"
      onAccept={() => {
        aceptar.mutate();
      }}
      onCancel={() => {
        void supabase.auth.signOut();
      }}
    />
  );
}
